/**
 * `/match/:tournamentId/:pairing` — turn-by-turn match recap.
 *
 * Loads a real bench archive from `/replays/<tournament>/matches/<pairing>/`:
 *   - `match.json`               → best-of-N outcome + draft + battle list
 *   - `battle_<i>.jsonl`         → per-turn engine frames (parsed via
 *                                  `parseBattle` → `ParsedBattle`)
 *   - `../transcripts/<agent>.jsonl` → LLM reasoning lines
 *
 * Renders the full-viewport spectator overlay (`MatchOverlay` from
 * `@pilot-protocol/visual-engine`) on top of the 2D hex backdrop —
 * side stat panels with real zone armor + heat + status badges, loadout
 * lists under the HUD, reasoning panels, telemetry feed, accolade
 * flashes, and a PRE/T01/T02/… turn-tab strip in the chrome header.
 */
import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Badge,
  Button,
  HexGridRenderer,
  type MechView,
} from "@pilot-protocol/ui";
import {
  CosmeticPortrait,
  DEFAULT_TOKENS,
  MatchOverlay,
  type OverlayPanel,
  type TranscriptEntry,
} from "@pilot-protocol/visual-engine";
import type {
  MechState,
  ParsedBattle,
} from "@pilot-protocol/replay-parser";
import {
  loadBattle,
  loadMatch,
  loadTranscript,
  loadoutFromDraft,
  type EngineMatch,
  type LoadoutItem,
} from "../data/bench";

// ---- Loaders --------------------------------------------------------

interface MatchAssets {
  match: EngineMatch;
  battles: ParsedBattle[];
  transcriptA: TranscriptEntry[];
  transcriptB: TranscriptEntry[];
  loadoutA: LoadoutItem[];
  loadoutB: LoadoutItem[];
}

async function loadAll(
  tournamentId: string,
  pairing: string,
): Promise<MatchAssets> {
  const archive = `matches/${pairing}`;
  const match = await loadMatch(tournamentId, archive);
  const battles = await Promise.all(
    match.battles.map((b) => loadBattle(tournamentId, archive, b.replay)),
  );
  const [transcriptA, transcriptB] = await Promise.all([
    loadTranscript(tournamentId, match.agent_a.name, match.match_seed).catch(
      () => [],
    ),
    loadTranscript(tournamentId, match.agent_b.name, match.match_seed).catch(
      () => [],
    ),
  ]);
  return {
    match,
    battles,
    transcriptA,
    transcriptB,
    loadoutA: loadoutFromDraft(match.agent_a.draft),
    loadoutB: loadoutFromDraft(match.agent_b.draft),
  };
}

// ---- Rendering helpers ----------------------------------------------

function LoadoutList({ tools }: { tools: ReadonlyArray<LoadoutItem> }) {
  if (!tools.length) return null;
  return (
    <>
      <p style={loadoutHeaderStyle}>LOADOUT</p>
      <ul style={loadoutListStyle}>
        {tools.map((t, i) => (
          <li key={`${t.name}-${i}`} style={loadoutRowStyle}>
            <span style={loadoutNameStyle}>{t.label}</span>
          </li>
        ))}
      </ul>
    </>
  );
}

function initialsFor(name: string): string {
  return name
    .split(/[\s_]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
}

// HexGridRenderer is Pixi-backed; it can't resolve CSS variables.
// Mirror the chrome tokens (`--pp-accent-a/b` in
// `packages/theme/src/chrome/chrome.css`) as concrete hex literals.
const ACCENT_A_HEX = "#9ec5d6";
const ACCENT_B_HEX = "#d6a07a";

function mechView(state: MechState, side: "A" | "B"): MechView {
  return {
    pos: { q: state.position.q, r: state.position.r },
    facing: state.facing,
    color: side === "A" ? ACCENT_A_HEX : ACCENT_B_HEX,
    label: side,
  };
}

const PANELS: OverlayPanel[] = [
  "metadata",
  "statA",
  "statB",
  "reasoningA",
  "reasoningB",
  "telemetry",
  "accolades",
];

// ---- Component ------------------------------------------------------

export default function MatchPage() {
  const { tournamentId = "", pairing = "" } = useParams<{
    tournamentId: string;
    pairing: string;
  }>();
  const navigate = useNavigate();

  const [assets, setAssets] = useState<MatchAssets | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tournamentId || !pairing) return;
    let cancelled = false;
    setAssets(null);
    setError(null);
    (async () => {
      try {
        const next = await loadAll(tournamentId, pairing);
        if (!cancelled) setAssets(next);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tournamentId, pairing]);

  const [battleIndex, setBattleIndex] = useState(0);
  useEffect(() => setBattleIndex(0), [tournamentId, pairing]);

  const battle = assets?.battles[battleIndex] ?? null;
  const maxTick = Math.max(0, (battle?.states.length ?? 1) - 1);

  const [tick, setTick] = useState(0);
  useEffect(() => setTick(0), [battleIndex, tournamentId, pairing]);

  const tabs = useMemo(() => {
    if (!battle) return [] as { key: string; label: string; tick: number }[];
    return battle.states.map((_s, idx) => ({
      key: idx === 0 ? "pre" : `t${idx}`,
      label: idx === 0 ? "PRE" : `T${String(idx).padStart(2, "0")}`,
      tick: idx,
    }));
  }, [battle]);

  const activeTabIdx = Math.max(
    0,
    tabs.findIndex((t) => t.tick === Math.min(tick, maxTick)),
  );

  if (error) {
    return (
      <div style={notFoundStyle}>
        <Badge variant="destructive">Failed</Badge>
        <h1 style={titleStyle}>Match load error</h1>
        <p style={leadStyle}>
          {error} —{" "}
          <Link to={`/tournament/${tournamentId}`} style={{ color: "var(--foreground)" }}>
            back to tournament
          </Link>
          .
        </p>
      </div>
    );
  }

  if (!assets || !battle) {
    return (
      <div style={notFoundStyle}>
        <p style={leadStyle}>Loading…</p>
      </div>
    );
  }

  const state = battle.states[Math.min(tick, maxTick)];
  const matchScore =
    assets.match.best_of > 1
      ? {
          winsA: assets.match.outcome.wins_a,
          winsB: assets.match.outcome.wins_b,
          bestOf: assets.match.best_of,
        }
      : undefined;

  return (
    <div style={pageStyle}>
      <header style={chromeHeaderStyle}>
        <nav style={crumbStyle}>
          <Link to="/tournaments" style={crumbLinkStyle}>
            Tournaments
          </Link>
          <span>›</span>
          <Link to={`/tournament/${tournamentId}`} style={crumbLinkStyle}>
            {tournamentId.toUpperCase()}
          </Link>
          <span>›</span>
          <span>
            {assets.match.agent_a.name.toUpperCase()} vs{" "}
            {assets.match.agent_b.name.toUpperCase()}
          </span>
        </nav>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {assets.match.battles.length > 1 ? (
            <div style={battleNavStyle}>
              <span style={battleNavLabelStyle}>BATTLE</span>
              {assets.match.battles.map((b, idx) => (
                <button
                  type="button"
                  key={b.index}
                  onClick={() => setBattleIndex(idx)}
                  style={battleBtnStyle(idx === battleIndex)}
                  aria-pressed={idx === battleIndex}
                >
                  {b.index + 1}
                </button>
              ))}
            </div>
          ) : null}
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              navigate(`/replay/${assets.match.match_id}?battle=${battleIndex}`)
            }
          >
            ▶ Replay viewer
          </Button>
        </div>
      </header>

      <nav style={tabStripStyle} aria-label="Turn navigator">
        <button
          type="button"
          style={tabArrowStyle(activeTabIdx === 0)}
          onClick={() => {
            if (activeTabIdx > 0) setTick(tabs[activeTabIdx - 1].tick);
          }}
          disabled={activeTabIdx === 0}
          aria-label="Previous turn"
        >
          ‹
        </button>
        {tabs.map((t, i) => (
          <button
            key={t.key}
            type="button"
            style={tabBtnStyle(i === activeTabIdx)}
            onClick={() => setTick(t.tick)}
            aria-pressed={i === activeTabIdx}
          >
            {t.label}
          </button>
        ))}
        <button
          type="button"
          style={tabArrowStyle(activeTabIdx >= tabs.length - 1)}
          onClick={() => {
            if (activeTabIdx < tabs.length - 1)
              setTick(tabs[activeTabIdx + 1].tick);
          }}
          disabled={activeTabIdx >= tabs.length - 1}
          aria-label="Next turn"
        >
          ›
        </button>
        <span style={tabStripSpacerStyle}>
          {tabs[activeTabIdx]?.label ?? "—"} · turn {tick} / {maxTick}
        </span>
      </nav>

      <div style={stageStyle}>
        <div style={hexBackdropStyle} aria-hidden>
          <HexGridRenderer
            mapRadius={state.map_radius}
            mechA={mechView(state.mech_a, "A")}
            mechB={mechView(state.mech_b, "B")}
            hexSize={36}
            width={1100}
            height={700}
          />
        </div>

        <MatchOverlay
          battle={battle}
          tick={tick}
          panels={PANELS}
          tokens={DEFAULT_TOKENS}
          score={matchScore}
          cosmeticA={
            <CosmeticPortrait
              initials={initialsFor(assets.match.agent_a.name)}
              accent="var(--pp-accent-a)"
            />
          }
          cosmeticB={
            <CosmeticPortrait
              initials={initialsFor(assets.match.agent_b.name)}
              accent="var(--pp-accent-b)"
            />
          }
          loadoutA={<LoadoutList tools={assets.loadoutA} />}
          loadoutB={<LoadoutList tools={assets.loadoutB} />}
          transcriptA={assets.transcriptA}
          transcriptB={assets.transcriptB}
          showReasoningStats
        />
      </div>
    </div>
  );
}

// ---- Styles ----------------------------------------------------------

const pageStyle: CSSProperties = {
  position: "fixed",
  inset: 0,
  display: "flex",
  flexDirection: "column",
  background: "#050505",
  color: "var(--pp-text)",
  fontFamily: "var(--pp-font-family)",
};

const chromeHeaderStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "8px 16px",
  borderBottom: "1px solid var(--pp-border)",
  background: "rgba(0, 0, 0, 0.6)",
  gap: 12,
};

const crumbStyle: CSSProperties = {
  display: "flex",
  gap: 6,
  alignItems: "center",
  fontFamily: "var(--pp-font-family)",
  fontSize: "var(--pp-font-sm)",
  color: "var(--pp-text-muted)",
  textTransform: "uppercase",
  letterSpacing: "var(--pp-tracking-caps)",
};

const crumbLinkStyle: CSSProperties = {
  color: "inherit",
  textDecoration: "none",
};

const battleNavStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
};

const battleNavLabelStyle: CSSProperties = {
  fontSize: "var(--pp-font-sm)",
  color: "var(--pp-text-muted)",
  textTransform: "uppercase",
  letterSpacing: "var(--pp-tracking-caps)",
};

const battleBtnStyle = (active: boolean): CSSProperties => ({
  minWidth: 28,
  height: 24,
  padding: "0 8px",
  border: `1px solid ${active ? "var(--pp-text)" : "var(--pp-border)"}`,
  background: active ? "var(--pp-text)" : "transparent",
  color: active ? "var(--pp-text-on-accent)" : "var(--pp-text-muted)",
  fontFamily: "inherit",
  fontSize: "var(--pp-font-sm)",
  letterSpacing: "var(--pp-tracking-caps)",
  cursor: "pointer",
});

const tabStripStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 4,
  padding: "8px 16px",
  borderBottom: "1px solid var(--pp-border)",
  background: "rgba(0, 0, 0, 0.6)",
  overflowX: "auto",
};

const tabArrowStyle = (disabled: boolean): CSSProperties => ({
  minWidth: 28,
  height: 26,
  padding: "0 8px",
  border: "1px solid var(--pp-border)",
  background: "transparent",
  color: disabled ? "var(--pp-text-muted)" : "var(--pp-text)",
  fontFamily: "inherit",
  fontSize: "var(--pp-font-sm)",
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.4 : 1,
});

const tabBtnStyle = (active: boolean): CSSProperties => ({
  minWidth: 40,
  height: 26,
  padding: "0 10px",
  border: `1px solid ${active ? "var(--pp-text)" : "var(--pp-border)"}`,
  background: active ? "var(--pp-text)" : "transparent",
  color: active ? "var(--pp-text-on-accent)" : "var(--pp-text-muted)",
  fontFamily: "inherit",
  fontSize: "var(--pp-font-sm)",
  fontWeight: "var(--pp-weight-bold)",
  letterSpacing: "var(--pp-tracking-caps)",
  cursor: "pointer",
  fontVariantNumeric: "tabular-nums",
});

const tabStripSpacerStyle: CSSProperties = {
  marginLeft: "auto",
  fontFamily: "var(--pp-font-family)",
  fontSize: "var(--pp-font-sm)",
  color: "var(--pp-text-muted)",
  textTransform: "uppercase",
  letterSpacing: "var(--pp-tracking-caps)",
  fontVariantNumeric: "tabular-nums",
};

const stageStyle: CSSProperties = {
  position: "relative",
  flex: 1,
  minHeight: 0,
  overflow: "hidden",
};

const hexBackdropStyle: CSSProperties = {
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background:
    "radial-gradient(ellipse at center, #15192d 0%, #060809 80%)",
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontFamily: "var(--font-display)",
  fontSize: "var(--text-3xl)",
};

const leadStyle: CSSProperties = {
  margin: 0,
  maxWidth: 640,
  color: "var(--foreground-secondary)",
  fontSize: "var(--text-base)",
  lineHeight: 1.5,
};

const notFoundStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 12,
  alignItems: "flex-start",
  padding: 32,
};

const loadoutHeaderStyle: CSSProperties = {
  margin: 0,
  fontSize: "var(--pp-font-sm)",
  textTransform: "uppercase",
  letterSpacing: "var(--pp-tracking-caps)",
  color: "var(--pp-text-muted)",
};

const loadoutListStyle: CSSProperties = {
  margin: 0,
  padding: 0,
  listStyle: "none",
  display: "flex",
  flexDirection: "column",
  gap: 2,
};

const loadoutRowStyle: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  fontSize: "var(--pp-font-sm)",
  color: "var(--pp-text)",
  fontVariantNumeric: "tabular-nums",
};

const loadoutNameStyle: CSSProperties = {
  textTransform: "uppercase",
  letterSpacing: "var(--pp-tracking-caps)",
};
